import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import axiosInstance from "../../../api/axiosInstance";
import AddAdjustment from "./AddAdjustment";

// ─── Theme (matches Pharmacy.jsx) ────────────────────────────────────────────
const C = { gray:'#5B6B79', blue:'#96C5F7', red:'#9E2B25', orange:'#F29E4C', green:'#2CA87F', teal:'#0891b2', purple:'#7c3aed' }
const thStyle = { fontSize:11, fontWeight:700, color:C.gray, textTransform:'uppercase', letterSpacing:'0.05em', padding:'8px 10px', background:'#f8f9fb', borderBottom:'1px solid #e8ecf0', whiteSpace:'nowrap' }
const tdStyle = { fontSize:13, color:'#2d3748', padding:'7px 10px', verticalAlign:'middle', borderBottom:'1px solid #f0f2f5' }
const inputStyle = { height:34, border:'1.5px solid #dde3eb', borderRadius:8, padding:'0 10px', fontSize:12, color:'#1a202c', outline:'none', boxSizing:'border-box', background:'#fff' }
const labelStyle = { fontSize:10, fontWeight:600, color:C.gray, display:'block', marginBottom:3, textTransform:'uppercase', letterSpacing:'0.04em' }

const TYPE_META = {
  stock_in:   { label:'Stock In',   bg:`${C.green}18`,  color:C.green  },
  stock_out:  { label:'Stock Out',  bg:`${C.red}18`,    color:C.red    },
  adjustment: { label:'Adjustment', bg:`${C.teal}18`,   color:C.teal   },
  write_off:  { label:'Write Off',  bg:`${C.orange}18`, color:C.orange },
  return:     { label:'Return',     bg:`${C.blue}22`,   color:'#4a90d9' },
  transfer:   { label:'Transfer',   bg:`${C.gray}18`,   color:C.gray   },
};

const PAGE_SIZE = 15;

export default function StockMovements() {
  const [movements, setMovements] = useState([]);
  const [drugs, setDrugs]         = useState([]);
  const [filters, setFilters]     = useState({ drug_id:"", movement_type:"", date_from:"", date_to:"" });
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [page, setPage]           = useState(1);

  const fetchMovements = async () => {
    try {
      const params = {};
      if (filters.drug_id)       params.drug_id = filters.drug_id;
      if (filters.movement_type) params.movement_type = filters.movement_type;
      if (filters.date_from)     params.date_from = filters.date_from;
      if (filters.date_to)       params.date_to = filters.date_to;
      const res = await axiosInstance.get("getStockMovements", { params });
      setMovements(res.data.data || []);
      setPage(1);
    } catch { Swal.fire("Error", "Failed to load stock movements", "error"); }
  };

  const fetchDrugs = async () => {
    try { const res = await axiosInstance.get("getDrugs"); setDrugs(res.data.data || []); } catch {}
  };

  useEffect(() => { fetchDrugs(); fetchMovements(); }, []);

  const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });

  const paged      = movements.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(movements.length / PAGE_SIZE);

  if (adjustOpen) return <AddAdjustment drugs={drugs} onClose={() => { setAdjustOpen(false); fetchMovements(); }} />;

  return (
    <>
      {/* ── Filter Bar ── */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:10, alignItems:'flex-end', marginBottom:14, padding:'12px 14px', background:'#f8f9fb', borderRadius:8, border:'1px solid #e8ecf0' }}>
        <div style={{ minWidth:160 }}>
          <label style={labelStyle}>Drug</label>
          <select name="drug_id" value={filters.drug_id} onChange={handleFilterChange}
            style={{ ...inputStyle, width:'100%' }}
            onFocus={e => e.target.style.borderColor = C.teal} onBlur={e => e.target.style.borderColor = '#dde3eb'}>
            <option value="">All Drugs</option>
            {drugs.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div style={{ minWidth:140 }}>
          <label style={labelStyle}>Type</label>
          <select name="movement_type" value={filters.movement_type} onChange={handleFilterChange}
            style={{ ...inputStyle, width:'100%' }}
            onFocus={e => e.target.style.borderColor = C.teal} onBlur={e => e.target.style.borderColor = '#dde3eb'}>
            <option value="">All Types</option>
            {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>From</label>
          <input type="date" name="date_from" value={filters.date_from} onChange={handleFilterChange}
            style={{ ...inputStyle, width:140 }}
            onFocus={e => e.target.style.borderColor = C.teal} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
        </div>
        <div>
          <label style={labelStyle}>To</label>
          <input type="date" name="date_to" value={filters.date_to} onChange={handleFilterChange}
            style={{ ...inputStyle, width:140 }}
            onFocus={e => e.target.style.borderColor = C.teal} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
        </div>
        <button onClick={fetchMovements}
          style={{ height:34, padding:'0 14px', fontSize:12, borderRadius:8, border:`1.5px solid ${C.teal}`, background:`${C.teal}18`, color:C.teal, cursor:'pointer', fontWeight:600, display:'flex', alignItems:'center', gap:5 }}>
          <i className="ti ti-filter" style={{ fontSize:13 }} /> Filter
        </button>
        <div style={{ flex:1 }} />
        <span style={{ fontSize:11, color:C.gray, alignSelf:'center' }}>{movements.length} record{movements.length !== 1 ? 's' : ''}</span>
        <button onClick={() => setAdjustOpen(true)}
          style={{ height:34, padding:'0 14px', fontSize:12, borderRadius:8, border:`1.5px solid ${C.orange}`, background:`${C.orange}18`, color:C.orange, cursor:'pointer', fontWeight:600, display:'flex', alignItems:'center', gap:5 }}>
          <i className="ti ti-adjustments" style={{ fontSize:13 }} /> Manual Adjustment
        </button>
      </div>

      {/* ── Table ── */}
      <div style={{ overflowX:'auto', borderRadius:8, border:'1px solid #e8ecf0' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, borderLeft:`3px solid ${C.gray}` }}>#</th>
              <th style={thStyle}>Drug</th>
              <th style={thStyle}>Batch #</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Quantity</th>
              <th style={thStyle}>Reference</th>
              <th style={thStyle}>Notes</th>
              <th style={thStyle}>Date</th>
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ ...tdStyle, textAlign:'center', color:C.gray, padding:'36px 0' }}>
                  <i className="ti ti-database-off" style={{ fontSize:28, display:'block', marginBottom:6, opacity:0.4 }} />
                  No stock movements found
                </td>
              </tr>
            ) : paged.map((item, i) => {
              const t = TYPE_META[item.movement_type] || { label: item.movement_type, bg:`${C.gray}18`, color:C.gray };
              const qtyColor = item.quantity > 0 ? C.green : C.red;
              return (
                <tr
                  key={item.id}
                  style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}
                  onMouseEnter={e => e.currentTarget.style.background = `${C.gray}0d`}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}
                >
                  <td style={tdStyle}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                  <td style={{ ...tdStyle, fontWeight:500 }}>{item.drug_name}</td>
                  <td style={tdStyle}>
                    {item.batch_number
                      ? <span style={{ fontFamily:'monospace', fontSize:12, background:'#f1f3f5', padding:'2px 6px', borderRadius:4 }}>{item.batch_number}</span>
                      : '—'}
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, background:t.bg, color:t.color }}>{t.label}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontWeight:700, color:qtyColor }}>
                      {item.quantity > 0 ? `+${item.quantity}` : item.quantity}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontSize:12, color:C.gray }}>
                    {item.reference_type
                      ? `${item.reference_type.replace(/_/g, ' ')}${item.reference_id ? ` #${item.reference_id}` : ''}`
                      : '—'}
                  </td>
                  <td style={{ ...tdStyle, fontSize:12, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.notes || '—'}</td>
                  <td style={{ ...tdStyle, fontSize:12 }}>{new Date(item.created_at).toLocaleDateString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12, padding:'8px 4px' }}>
          <span style={{ fontSize:12, color:C.gray }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, movements.length)} of {movements.length}
          </span>
          <div style={{ display:'flex', gap:4 }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              style={{ height:28, padding:'0 10px', fontSize:12, borderRadius:6, border:'1px solid #dde3eb', background: page === 1 ? '#f8f9fb' : '#fff', color: page === 1 ? C.gray : '#2d3748', cursor: page === 1 ? 'default' : 'pointer' }}>
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => Math.abs(p - page) <= 2)
              .map(p => (
                <button key={p} onClick={() => setPage(p)}
                  style={{ height:28, width:28, fontSize:12, borderRadius:6, border:`1px solid ${p === page ? C.teal : '#dde3eb'}`, background: p === page ? C.teal : '#fff', color: p === page ? '#fff' : '#2d3748', cursor:'pointer', fontWeight: p === page ? 700 : 400 }}>
                  {p}
                </button>
              ))}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              style={{ height:28, padding:'0 10px', fontSize:12, borderRadius:6, border:'1px solid #dde3eb', background: page === totalPages ? '#f8f9fb' : '#fff', color: page === totalPages ? C.gray : '#2d3748', cursor: page === totalPages ? 'default' : 'pointer' }}>
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
}
